import React from 'react';
import { IonContent, IonPage } from '@ionic/react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import Reveal from '../components/Reveal';

type Link = { label: string; url: string };
type Project = { kicker: string; title: string; description: string; tech: string[]; links: Link[] };

const aiProjects: Project[] = [
  {
    kicker: 'Multi-Agent Systems',
    title: 'Keepsake — Ten-Agent Voice Companion for Dementia Care',
    description:
      'A voice-first memory companion for a person living with dementia, plus a portal for her family. Ten cooperating agents run her day — but every decision that could hurt her if wrong is made by code, not a prompt: medication questions never reach a model, nightly agent-rewritten prompts only go live after passing coded checks, and a code-writing agent can open pull requests but never merge them. Deployed on Google Cloud Run.',
    tech: ['Multi-Agent Systems', 'Gemini', 'Voice UI', 'Google Cloud Run', 'AI Safety Guardrails'],
    links: [
      { label: 'Live App ↗', url: 'https://keepsake-776070314074.europe-west1.run.app' },
      { label: 'Video Demo ↗', url: 'https://vimeo.com/1222237493' }
    ]
  },
  {
    kicker: 'Agentic Commerce',
    title: 'Meshyay \u2014 Commerce Platform Run by Bounded AI Agents',
    description:
      'A commerce platform built end to end: one Flutter codebase serving web, Android and iOS across three surfaces \u2014 shopper storefront, supplier portal, and admin console \u2014 on a FastAPI backend deployed to Cloud Run, localised for English, Arabic and Urdu with full RTL. Its back office is run by AI agents, and what an agent may do without a human is an explicit autonomy register in code rather than a deployment boundary: agents draft campaigns and queue them, a human sends. A companion project settles supplier payouts autonomously in USDC through Circle Agent Wallet within a human-set policy cap, built for the Build with Gemini XPRIZE (Agentic Economy Prize).',
    tech: ['Flutter', 'FastAPI', 'Google Cloud Run', 'AI Agents', 'Circle Agent Wallet', 'USDC'],
    links: [
      { label: 'meshyay.com \u2197', url: 'https://meshyay.com' },
      { label: 'USDC agent on GitHub \u2197', url: 'https://github.com/mujahidmasood/meshyay-agentic-usdc' }
    ]
  }
];

const products: Project[] = [
  {
    kicker: 'Marketplace',
    title: 'Tijarat360',
    description:
      'A full-stack, polyglot B2B digital marketplace for the agricultural sector in Pakistan: React/Capacitor mobile frontend, FastAPI asynchronous backend, Polygon smart contracts for escrow, and Firebase for SMS and notifications.',
    tech: ['React', 'Capacitor', 'FastAPI', 'MongoDB', 'Solidity', 'Firebase'],
    links: [
      { label: 'Web ↗', url: 'https://tijarat360.com' },
      { label: 'Android ↗', url: 'https://play.google.com/store/apps/details?id=io.tijarat360.app' },
      { label: 'iOS ↗', url: 'https://apps.apple.com/de/app/tijarat-360/id6758799087?l=en-GB' }
    ]
  },
  {
    kicker: 'Game',
    title: 'Builder Breakers',
    description:
      'A strategic puzzle and tower-defense game with custom levels, multiplayer tournaments, and an AI-driven game economy.',
    tech: ['Unity', 'C#', 'Firebase'],
    links: [
      { label: 'Web ↗', url: 'https://builderbreakers.fun' },
      { label: 'Android ↗', url: 'https://play.google.com/store/apps/details?id=com.innovpk.app.orderchaos' },
      { label: 'iOS ↗', url: 'https://apps.apple.com/de/app/builder-breaker/id6758314501?l=en-GB' }
    ]
  },
  {
    kicker: 'Game',
    title: 'Kabutar Ud',
    description:
      'A cross-platform, multi-language interactive game built with Flutter: real-time multiplayer synced via Firebase, text-to-speech integration, and Google Mobile Ads monetization.',
    tech: ['Flutter', 'Dart', 'Firebase RTDB', 'TTS'],
    links: [
      { label: 'Web ↗', url: 'https://kabutar.chatwithme.fun' },
      { label: 'Android ↗', url: 'https://play.google.com/store/apps/details?id=com.innovpk.apps.game.kabutarud' },
      { label: 'iOS ↗', url: 'https://apps.apple.com/de/app/kabutar-ud/id6759179663?l=en-GB' }
    ]
  },
  {
    kicker: 'ML Research',
    title: 'Automated Exam Grader',
    description:
      'Master’s thesis at TU Darmstadt: automating exam grading with machine learning and computer vision.',
    tech: ['Java', 'Python', 'Machine Learning', 'Computer Vision'],
    links: [{ label: 'GitHub ↗', url: 'https://github.com/mujahidmasood/automated-exam-grader' }]
  }
];

const ProjectCard: React.FC<{ project: Project; violet?: boolean }> = ({ project, violet }) => (
  <div className={`card ${violet ? 'card--violet' : 'card--accent'}`}>
    <span className="card__kicker">{project.kicker}</span>
    <h2 className="card__title">{project.title}</h2>
    <p className="card__body">{project.description}</p>
    <div className="chips">
      {project.tech.map((t, i) => (
        <span className={`chip ${violet ? 'chip--violet' : ''}`} key={i}>{t}</span>
      ))}
    </div>
    {project.links.length > 0 && (
      <div className="card__links">
        {project.links.map((link, i) => (
          <a className="card__link" key={i} href={link.url} target="_blank" rel="noreferrer">
            {link.label}
          </a>
        ))}
      </div>
    )}
  </div>
);

const Projects: React.FC = () => (
  <IonPage>
    <IonContent>
      <SiteNav current="/projects" />
      <main className="page">
        <Reveal>
          <p className="eyebrow"><em>ls</em> ~/projects</p>
          <h1 className="display" style={{ fontSize: 'clamp(1.9rem, 5vw, 3rem)' }}>
            AI systems &amp; shipped products
          </h1>
          <p className="lede">
            Production-grade applied-AI work — multi-agent systems and agentic commerce —
            and consumer apps live on the Play Store and App Store.
          </p>
        </Reveal>

        <section className="section" style={{ marginTop: '3rem' }}>
          <Reveal>
            <p className="section-label"><em>ls</em> ai-agent-systems/</p>
          </Reveal>
          <div style={{ display: 'grid', gap: '1.25rem' }}>
            {aiProjects.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <ProjectCard project={p} violet />
              </Reveal>
            ))}
          </div>
        </section>

        <section className="section">
          <Reveal>
            <p className="section-label"><em>ls</em> shipped-products/</p>
          </Reveal>
          <div className="card-grid">
            {products.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <ProjectCard project={p} />
              </Reveal>
            ))}
          </div>
        </section>

        <SiteFooter />
      </main>
    </IonContent>
  </IonPage>
);

export default Projects;
